/**
 * PreviewLayoutHook
 *
 * Client-side collision-free positioning for preview loot labels.
 *
 * The server assigns rough positions, but it can't know the client's actual
 * container size (responsive) or the real rendered size of each label. This
 * hook measures DOM boxes and repositions items to avoid overlaps.
 */
export const PreviewLayoutHook = {
  mounted() {
    this._layoutKey = null;
    this._resizeTimer = null;

    this._onResize = () => {
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(() => {
        this.relayout({ force: true });
      }, 150);
    };

    window.addEventListener("resize", this._onResize);
    this.relayoutSoon({ force: true });
  },

  updated() {
    this.relayoutSoon({ force: false });
  },

  destroyed() {
    window.removeEventListener("resize", this._onResize);
    window.clearTimeout(this._resizeTimer);
  },

  relayoutSoon({ force }) {
    // Double RAF so we measure after LiveView has patched and the browser has
    // laid out the updated DOM.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.relayout({ force });
      });
    });
  },

  relayout({ force }) {
    const items = Array.from(this.el.querySelectorAll(".poe-item"));
    if (items.length === 0) {
      this.el.style.removeProperty("height");
      return;
    }

    const key = items
      .map((el) => el.getAttribute("title") || el.textContent || "")
      .join("|");

    if (!force && this._layoutKey === key) return;
    this._layoutKey = key;

    let containerRect = this.el.getBoundingClientRect();

    if (containerRect.width >= 640 && this.el.style.height) {
      this.el.style.removeProperty("height");
      containerRect = this.el.getBoundingClientRect();
    }

    const W = containerRect.width;
    const H = containerRect.height;
    if (W <= 0 || H <= 0) return;

    // Keep a bit of breathing room around the edges and the top overlays.
    const safePadding = { top: 56, right: 16, bottom: 16, left: 16 };
    const bounds = {
      x: safePadding.left,
      y: safePadding.top,
      w: Math.max(0, W - safePadding.left - safePadding.right),
      h: Math.max(0, H - safePadding.top - safePadding.bottom),
    };

    const margin = 6;
    const placed = [];
    const blocked = getBlockedZones(this.el.dataset.blockZones, W, H);

    const measured = items.map((el) => {
      const r = el.getBoundingClientRect();
      return { el, w: r.width, h: r.height, area: r.width * r.height };
    });

    if (W < 640) {
      const mobileLayout = layoutMobileItems(
        measured,
        W,
        safePadding,
        margin,
      );

      this.el.style.height = `${Math.max(mobileLayout.height, 320)}px`;

      measured.forEach((item, index) => {
        const pos = mobileLayout.positions[index];
        item.el.style.left = `${pos.x}px`;
        item.el.style.top = `${pos.y}px`;
      });

      return;
    }

    const measuredByArea = measured
      // Place larger labels first to improve packing success.
      .sort((a, b) => b.area - a.area);

    for (const item of measuredByArea) {
      const pos = findPosition(item.w, item.h, bounds, placed, blocked, margin);
      placed.push({ x: pos.x, y: pos.y, w: item.w, h: item.h });

      item.el.style.left = `${(pos.x / W) * 100}%`;
      item.el.style.top = `${(pos.y / H) * 100}%`;
    }
  },
};

export function layoutMobileItems(items, width, padding, margin) {
  let nextY = padding.top;

  const positions = items.map((item) => {
    const position = {
      x: Math.max(0, (width - item.w) / 2),
      y: nextY,
    };

    nextY += item.h + margin;
    return position;
  });

  const contentHeight =
    positions.length === 0 ? padding.top : nextY - margin;

  return {
    height: contentHeight + padding.bottom,
    positions,
  };
}

function findPosition(w, h, bounds, placed, blocked, margin) {
  const maxX = bounds.x + Math.max(0, bounds.w - w);
  const maxY = bounds.y + Math.max(0, bounds.h - h);

  // Random sampling.
  const tries = 450;
  for (let i = 0; i < tries; i++) {
    const x = bounds.x + Math.random() * Math.max(1, maxX - bounds.x);
    const y = bounds.y + Math.random() * Math.max(1, maxY - bounds.y);
    if (!intersectsAny(x, y, w, h, placed, blocked, margin)) return { x, y };
  }

  // Grid scan fallback (still randomized via phase offsets).
  const step = 10;
  const phaseX = bounds.x + Math.random() * step;
  const phaseY = bounds.y + Math.random() * step;

  for (let y = phaseY; y <= maxY; y += step) {
    for (let x = phaseX; x <= maxX; x += step) {
      if (!intersectsAny(x, y, w, h, placed, blocked, margin)) return { x, y };
    }
  }

  // If we couldn't place, relax margin and try a tighter scan.
  if (margin > 0) return findPosition(w, h, bounds, placed, blocked, 0);

  // Last resort: clamp inside bounds.
  return { x: bounds.x, y: bounds.y };
}

function intersectsAny(x, y, w, h, placed, blocked, margin) {
  for (const p of placed) {
    const separated =
      x + w + margin <= p.x ||
      p.x + p.w + margin <= x ||
      y + h + margin <= p.y ||
      p.y + p.h + margin <= y;
    if (!separated) return true;
  }

  for (const b of blocked) {
    const separated =
      x + w + margin <= b.x ||
      b.x + b.w + margin <= x ||
      y + h + margin <= b.y ||
      b.y + b.h + margin <= y;
    if (!separated) return true;
  }

  return false;
}

function getBlockedZones(raw, width, height) {
  if (!raw) return [];

  try {
    const zones = JSON.parse(raw);
    if (!Array.isArray(zones)) return [];

    return zones
      .filter((zone) => zone && Number.isFinite(zone.x))
      .map((zone) => {
        const x = clamp((zone.x / 100) * width, 0, width);
        const y = clamp((zone.y / 100) * height, 0, height);
        const w = clamp((zone.w / 100) * width, 0, width);
        const h = clamp((zone.h / 100) * height, 0, height);
        return { x, y, w, h };
      })
      .filter((zone) => zone.w > 0 && zone.h > 0);
  } catch (_err) {
    return [];
  }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}
